import { parseCookies, setCookie } from 'nookies'
import { redactTokenUrl } from './capabilityPrivacy'

export const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content'
] as const

// Ad platform click identifiers appended to landing page URLs
export const CLICK_ID_PARAMS = [
  'gclid', // Google Ads
  'fbclid', // Meta (Facebook/Instagram)
  'msclkid' // Microsoft Ads (Bing)
] as const

/**
 * HubSpot Ads auto-tagging params. When the HubSpot Ads integration tags
 * outbound ad URLs (instead of explicit utm_*), it appends these. We map
 * them into utm_* at capture time so downstream attribution (cookies,
 * server-side classifier, marketing report) sees a consistent shape.
 * Explicit utm_* always wins — this is only a fallback.
 */
export const HSA_PARAMS = [
  'hsa_src', // ad source (short code — "g" google, "f" facebook, etc.)
  'hsa_net', // ad network name (adwords, facebook, linkedin, bing)
  'hsa_cam', // campaign id
  'hsa_grp', // ad group / adset id
  'hsa_ad', // ad id / creative id
  'hsa_kw', // keyword
  'hsa_tgt', // keyword/placement target
  'hsa_mt', // match type
  'hsa_acc', // HubSpot Ads account id
  'hsa_ver' // HubSpot Ads version
] as const

export type UTMParam = (typeof UTM_PARAMS)[number]
export type ClickIdParam = (typeof CLICK_ID_PARAMS)[number]
export type HsaParam = (typeof HSA_PARAMS)[number]

/**
 * Subset of `hsa_*` we persist as raw cookies (in addition to the
 * `utm_*` projection in `deriveUtmsFromHsa`). These carry data the utm_*
 * shape cannot express:
 *  - `hsa_mt` → bid match type (`e` exact / `p` phrase / `b` broad)
 *  - `hsa_grp` → ad group id (utm_campaign already holds the campaign id
 *    via `hsa_cam`, so we'd lose ad-group granularity without this)
 *  - `hsa_net` → ad network short name (`adwords` / `facebook` / `bing` /
 *    `linkedin`). Distinct from the classifier's `classified_source`
 *    because it's what HubSpot Ads actually said, not what we inferred.
 *  - `hsa_ad` → ad / creative id. Already projected to utm_content but
 *    keeping the raw key makes PostHog queries unambiguous when
 *    utm_content is also set explicitly.
 *
 * Adding `hsa_acc` / `hsa_ver` / `hsa_tgt` would be noise — they don't
 * help campaign-level slicing.
 */
export const HSA_KEEP_RAW = ['hsa_mt', 'hsa_grp', 'hsa_net', 'hsa_ad'] as const
export type HsaKeepRawParam = (typeof HSA_KEEP_RAW)[number]

/**
 * Map HubSpot `hsa_net` values to {source, medium} pairs that match the
 * server-side classifier conventions in `classifyAttribution.ts`.
 */
const HSA_NETWORK_MAP: Record<string, { source: string; medium: string }> = {
  adwords: { source: 'google', medium: 'cpc' },
  google: { source: 'google', medium: 'cpc' },
  facebook: { source: 'facebook', medium: 'paid-social' },
  instagram: { source: 'facebook', medium: 'paid-social' },
  linkedin: { source: 'linkedin', medium: 'paid-social' },
  bing: { source: 'bing', medium: 'cpc' },
  microsoft: { source: 'bing', medium: 'cpc' }
}

const readParam = (
  query: Record<string, string | string[] | undefined>,
  key: string
): string | undefined => {
  const val = query[key]
  if (Array.isArray(val)) return val[0]
  return val || undefined
}

/**
 * Derive utm_* values from hsa_* params. Returns only the subset we can
 * map with confidence — never fabricates medium/source for unknown
 * networks.
 */
export const deriveUtmsFromHsa = (
  query: Record<string, string | string[] | undefined>
): Partial<Record<UTMParam, string>> => {
  const derived: Partial<Record<UTMParam, string>> = {}

  const hsaNet = readParam(query, 'hsa_net')?.toLowerCase()
  if (hsaNet) {
    const mapped = HSA_NETWORK_MAP[hsaNet]
    if (mapped) {
      derived.utm_source = mapped.source
      derived.utm_medium = mapped.medium
    } else {
      // Unknown network — keep transparency without guessing medium
      derived.utm_source = hsaNet
      derived.utm_medium = 'paid'
    }
  }

  const hsaCam = readParam(query, 'hsa_cam')
  if (hsaCam) derived.utm_campaign = hsaCam

  const hsaKw = readParam(query, 'hsa_kw')
  if (hsaKw) derived.utm_term = hsaKw

  const hsaAd = readParam(query, 'hsa_ad')
  if (hsaAd) derived.utm_content = hsaAd

  return derived
}

export interface UTMData {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  utm_page?: string
  referrer?: string
  landing_timestamp?: string
  gclid?: string
  fbclid?: string
  msclkid?: string
  hsa_mt?: string
  hsa_grp?: string
  hsa_net?: string
  hsa_ad?: string
}

const COOKIE_OPTIONS = { maxAge: 30 * 24 * 60 * 60, path: '/' }

/**
 * Get all UTM data from cookies
 */
export function getUTMsFromCookies(): UTMData {
  if (typeof window === 'undefined') return {}

  const cookies = parseCookies()
  const utmData: UTMData = {}

  UTM_PARAMS.forEach((param) => {
    if (cookies[param]) {
      utmData[param] = cookies[param]
    }
  })

  CLICK_ID_PARAMS.forEach((param) => {
    if (cookies[param]) {
      utmData[param] = cookies[param]
    }
  })

  HSA_KEEP_RAW.forEach((param) => {
    if (cookies[param]) {
      utmData[param] = cookies[param]
    }
  })

  // Redacted on READ too: cookies written before 2026-09-23 can still hold a
  // bearer token, and these ride every GA4 event + PostHog super-property.
  if (cookies.utm_page) utmData.utm_page = redactTokenUrl(cookies.utm_page)
  if (cookies.utm_referrer)
    utmData.referrer = redactTokenUrl(cookies.utm_referrer)
  if (cookies.utm_landing_timestamp)
    utmData.landing_timestamp = cookies.utm_landing_timestamp

  return utmData
}

/**
 * Ad-platform click IDs we embed in WhatsApp prefills so the LangGraph
 * `complete` node can fire the server-side `qualified_whatsapp_lead`
 * conversion against the right platform. The server extractor
 * (`spotme-server/.../attributionUtils.ts`) reads these straight from the
 * listing URL's query string in the inbound WA message — so all the client
 * has to do is keep them on the prefill URL.
 */
export type ClickIds = { gclid?: string; fbclid?: string; msclkid?: string }

/**
 * Read the stored ad-platform click IDs from cookies. SSR-safe (returns
 * `{}` when there's no window). Only present IDs are included, so organic
 * visitors (no click ID) yield `{}` and the prefill URL stays unchanged.
 */
export function getClickIdsFromCookies(): ClickIds {
  const { gclid, fbclid, msclkid } = getUTMsFromCookies()
  const clickIds: ClickIds = {}
  if (gclid) clickIds.gclid = gclid
  if (fbclid) clickIds.fbclid = fbclid
  if (msclkid) clickIds.msclkid = msclkid
  return clickIds
}

/**
 * Click IDs from the stored cookies, overridden by any on the CURRENT URL.
 * The URL read matters on the landing page view itself: child effects run
 * before `<UTMs />` writes the cookies, so a mount-time cookie read there sees
 * nothing — or a returning visitor's PREVIOUS click (the same trap the Apollo
 * authLink works around — root CLAUDE.md → "`<UTMs />` cookie-timing
 * fallback"). The URL wins, matching `storeUTMs` (last click wins); after
 * intra-site navigation the URL carries no click ID, so the cookie is used.
 */
export function getClickIdsWithUrlFallback(): ClickIds {
  const clickIds = getClickIdsFromCookies()
  if (typeof window === 'undefined') return clickIds
  const params = new URLSearchParams(window.location.search)
  for (const param of CLICK_ID_PARAMS) {
    const value = params.get(param)
    if (value) clickIds[param] = value
  }
  return clickIds
}

/**
 * Check if there are any UTM params stored
 */
export function hasStoredUTMs(): boolean {
  const utms = getUTMsFromCookies()
  return UTM_PARAMS.some((param) => !!utms[param])
}

const isSameSiteHost = (hostname: string): boolean =>
  hostname === window.location.hostname ||
  hostname === 'spotme.mx' ||
  hostname.endsWith('.spotme.mx')

/**
 * `document.referrer` when it says where the visitor came FROM — undefined
 * when it is our own site. A same-site referrer only means this document was
 * reached by a document navigation from another SpotMe page (a Pages ↔ App
 * Router crossing, a new tab), which an SPA transition never produces; used
 * as attribution it turns a direct visitor into `(internal)` — for the whole
 * cookie window once `storeUTMs` persists it. Client-only: the SSR `Referer`
 * header is unchanged. An unparseable referrer carries no host to classify.
 */
export function getExternalReferrer(): string | undefined {
  if (typeof document === 'undefined' || !document.referrer) return undefined
  try {
    const { hostname } = new URL(document.referrer)
    return isSameSiteHost(hostname) ? undefined : document.referrer
  } catch {
    return undefined
  }
}

/**
 * Store UTM params from URL query to cookies
 */
export function storeUTMs(
  query: Record<string, string | string[] | undefined>,
  currentPath: string
): void {
  // HubSpot Ads auto-tagging uses hsa_* instead of utm_*. Map them first
  // so the rest of the flow treats them as normal UTMs. Explicit utm_*
  // always wins.
  const hsaDerived = deriveUtmsFromHsa(query)

  const resolveUtm = (param: UTMParam): string | undefined => {
    const explicit = readParam(query, param)
    return explicit ?? hsaDerived[param]
  }

  const hasNewUTMs = UTM_PARAMS.some((param) => !!resolveUtm(param))

  // Always capture click IDs when present (even without UTMs — e.g. Google
  // auto-tagging appends gclid without utm_* params)
  CLICK_ID_PARAMS.forEach((param) => {
    if (query[param]) {
      setCookie(null, param, String(query[param]), COOKIE_OPTIONS)
    }
  })

  // Persist the raw HubSpot Ads metadata we don't project into utm_*.
  // Same treatment as click IDs — always captured when present.
  HSA_KEEP_RAW.forEach((param) => {
    const value = readParam(query, param)
    if (value) {
      setCookie(null, param, value, COOKIE_OPTIONS)
    }
  })

  if (hasNewUTMs) {
    // Store each UTM param if present
    UTM_PARAMS.forEach((param) => {
      const value = resolveUtm(param)
      if (value) {
        setCookie(null, param, value, COOKIE_OPTIONS)
      }
    })

    // Store the landing page
    if (currentPath) {
      setCookie(null, 'utm_page', redactTokenUrl(currentPath), COOKIE_OPTIONS)
    }

    // Store the timestamp of when UTMs were captured
    setCookie(
      null,
      'utm_landing_timestamp',
      new Date().toISOString(),
      COOKIE_OPTIONS
    )

    // Store the referrer if available (never our own site)
    const referrer = getExternalReferrer()
    if (referrer) {
      setCookie(null, 'utm_referrer', redactTokenUrl(referrer), COOKIE_OPTIONS)
    }
  } else {
    // No UTMs in URL — capture organic/direct visit info, but only if not
    // already set. `hasStoredUTMs()` only checks utm_source/medium/etc, so
    // for direct visitors it always returns false; the previous code
    // overwrote utm_page on every navigation, which meant their FT
    // Landing Page in the marketing report was whatever the LAST internal
    // page they visited was (commonly /userConfirmation/sent-email).
    // First-touch fields must only be set once per session/cookie window.
    const cookies = parseCookies()
    if (currentPath && !cookies.utm_page) {
      setCookie(null, 'utm_page', redactTokenUrl(currentPath), COOKIE_OPTIONS)
    }
    if (!cookies.utm_landing_timestamp) {
      setCookie(
        null,
        'utm_landing_timestamp',
        new Date().toISOString(),
        COOKIE_OPTIONS
      )
    }
    const referrer = getExternalReferrer()
    if (!cookies.utm_referrer && referrer) {
      setCookie(null, 'utm_referrer', redactTokenUrl(referrer), COOKIE_OPTIONS)
    }
  }
}

/**
 * Get UTMs formatted for analytics events (flattened object)
 */
export function getUTMsForAnalytics(): Record<string, string> {
  const utms = getUTMsFromCookies()
  const result: Record<string, string> = {}

  Object.entries(utms).forEach(([key, value]) => {
    if (value) {
      result[key] = value
    }
  })

  return result
}
